import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipContentProps } from 'recharts/types/component/Tooltip';

import { ReportData } from '../types';
import { buildChartData, buildTooltipFormatter, buildYAxisFormatter } from '../utils/chart-data';
import { CHART_COLORS, TARGET_COLORS } from '../utils/format-measure';
import { ChartLegend } from './chart-legend';

const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 6,
  padding: '8px 10px',
  backgroundColor: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  color: 'hsl(var(--popover-foreground))',
  boxShadow: '0 2px 8px hsl(var(--border) / 0.4)',
};

interface ChartZone {
  from?: number;
  to?: number;
  color: string;
  label?: string;
}

interface ElementAreaChartProps {
  data: ReportData;
  hiddenSeries?: Set<string>;
  showAverageLine?: boolean;
  targets?: Array<{ label: string; value: number }>;
  zones?: ChartZone[];
}

export const ElementAreaChart = ({
  data,
  hiddenSeries,
  showAverageLine,
  targets,
  zones,
}: ElementAreaChartProps) => {
  const { t } = useTranslation('authenticated.reports');
  const { rows, measures } = buildChartData(data);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const tooltipFormatter = buildTooltipFormatter(data);

  const visibleMeasure = measures.find(m => !hiddenSeries?.has(m.key));
  const avgValue = showAverageLine && data.averageValue != null ? data.averageValue : null;
  const formattedAvg =
    avgValue != null ? buildYAxisFormatter(visibleMeasure?.type)(avgValue) : null;

  if (rows.length === 0 || measures.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{t('common.noData')}</p>;
  }

  const legendEntries = measures.map((m, i) => ({
    key: m.key,
    label: m.label,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <div className="grid h-full grid-rows-[1fr_auto]">
      <div className="min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <defs>
              {measures.map((m, i) => (
                <linearGradient key={m.key} id={`grad-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={CHART_COLORS[i % CHART_COLORS.length]}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLORS[i % CHART_COLORS.length]}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="__label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={buildYAxisFormatter(measures[0]?.type)}
              width={52}
            />
            <Tooltip
              content={(props: TooltipContentProps) => {
                const { active, payload, label } = props;
                if (!active || !payload?.length) return null;
                const items = (
                  hoveredKey ? payload.filter(p => String(p.dataKey) === hoveredKey) : payload
                ).filter(p => !hiddenSeries?.has(String(p.dataKey)));
                if (!items.length) return null;
                const visible = items.slice(0, 5);
                const overflow = items.length - 5;
                return (
                  <div style={TOOLTIP_STYLE}>
                    {label != null && (
                      <p
                        style={{
                          marginBottom: 4,
                          fontWeight: 600,
                          color: 'hsl(var(--muted-foreground))',
                        }}
                      >
                        {label}
                      </p>
                    )}
                    {visible.map(item => {
                      const [val, name] = tooltipFormatter(
                        item.value as number,
                        String(item.dataKey)
                      );
                      return (
                        <div
                          key={String(item.dataKey)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: item.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: 'hsl(var(--muted-foreground))' }}>{name}</span>
                          <span style={{ fontWeight: 500, marginLeft: 'auto', paddingLeft: 16 }}>
                            {val}
                          </span>
                        </div>
                      );
                    })}
                    {overflow > 0 && (
                      <p
                        style={{
                          marginTop: 4,
                          color: 'hsl(var(--muted-foreground))',
                          fontSize: 11,
                        }}
                      >
                        {t('common.overflowMore', { count: overflow })}
                      </p>
                    )}
                  </div>
                );
              }}
            />
            {zones?.map((zone, i) => (
              <ReferenceArea
                key={i}
                y1={zone.to}
                y2={zone.from}
                fill={zone.color}
                fillOpacity={1}
                strokeOpacity={0}
              />
            ))}
            {measures.map((m, i) => (
              <Area
                key={m.key}
                dataKey={m.key}
                name={m.label}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={hoveredKey === m.key ? 3 : 2}
                strokeOpacity={hoveredKey === null || hoveredKey === m.key ? 1 : 0.15}
                fillOpacity={hoveredKey === null || hoveredKey === m.key ? 1 : 0.05}
                fill={`url(#grad-${m.key})`}
                dot={false}
                activeDot={{ r: 4 }}
                hide={hiddenSeries?.has(m.key) ?? false}
                onMouseEnter={() => setHoveredKey(m.key)}
                onMouseLeave={() => setHoveredKey(null)}
              />
            ))}
            {avgValue != null && (
              <ReferenceLine
                y={avgValue}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: `${t('chart.avgPrefix')} ${formattedAvg}`,
                  position: 'insideTopRight',
                  fontSize: 11,
                  // eslint-disable-next-line i18next/no-literal-string
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            )}
            {targets?.map((target, i) => (
              <ReferenceLine
                key={i}
                y={target.value}
                stroke={TARGET_COLORS[i % TARGET_COLORS.length]}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: target.label,
                  position: 'insideTopRight',
                  fontSize: 11,
                  fill: TARGET_COLORS[i % TARGET_COLORS.length],
                }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <ChartLegend
        entries={legendEntries}
        hiddenSeries={hiddenSeries}
        hoveredKey={hoveredKey}
        onHover={setHoveredKey}
      />
    </div>
  );
};
